import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Trash2, FileText, Video, Plus, Presentation, Calendar, User, Building2, BookOpen, UserPlus, X, Search, BellRing, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

export function AdminKeynotesTab() {
  const [keynotes, setKeynotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [accessKeynote, setAccessKeynote] = useState(null);
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessSearch, setAccessSearch] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);
  // Pending access requests (member-initiated). Surfaced in a panel above
  // the keynote list so admins can grant/dismiss in one click. Without
  // this UI, requesters showed up only in the global notifications bell
  // and there was no way to see who had asked, when, or why.
  const [accessRequests, setAccessRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestBusyId, setRequestBusyId] = useState(null);
  const [form, setForm] = useState({
    title: '', presenter: '', company: '', event_name: '', event_date: '',
    subject: '', description: '', video_url: '', file: null,
  });

  const loadKeynotes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/keynotes/admin`, { credentials: 'include' });
      if (res.ok) setKeynotes(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadKeynotes(); }, [loadKeynotes]);

  const loadAccessRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/keynote-requests`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAccessRequests(data.requests || []);
      }
    } catch {}
    setRequestsLoading(false);
  }, []);

  useEffect(() => { loadAccessRequests(); }, [loadAccessRequests]);

  const handleGrantRequest = async (req) => {
    if (!req?.request_id) return;
    setRequestBusyId(req.request_id);
    try {
      const res = await fetch(`${API}/api/admin/keynote-requests/${req.request_id}/grant`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Access granted to ${req.current_name} (${data.keynotes_updated} keynote${data.keynotes_updated !== 1 ? 's' : ''})`);
        loadAccessRequests();
        loadKeynotes();
      } else {
        toast.error('Failed to grant access');
      }
    } catch { toast.error('Failed to grant access'); }
    setRequestBusyId(null);
  };

  const handleDismissRequest = async (req) => {
    if (!req?.request_id) return;
    setRequestBusyId(req.request_id);
    try {
      const res = await fetch(`${API}/api/admin/keynote-requests/${req.request_id}/dismiss`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success(`Dismissed request from ${req.current_name}`);
        loadAccessRequests();
      } else {
        toast.error('Failed to dismiss');
      }
    } catch { toast.error('Failed to dismiss'); }
    setRequestBusyId(null);
  };

  // Fetch member list lazily, only when the access dialog opens.
  const openAccessDialog = async (keynote) => {
    setAccessKeynote(keynote);
    setAccessSearch('');
    if (accessUsers.length === 0) {
      try {
        const res = await fetch(`${API}/api/admin/users`, { credentials: 'include' });
        if (res.ok) setAccessUsers(await res.json());
      } catch {}
    }
  };

  const grantAccess = async (userId) => {
    if (!accessKeynote) return;
    setAccessBusy(true);
    const kid = accessKeynote.keynote_id || accessKeynote.file_id;
    try {
      const res = await fetch(`${API}/api/admin/keynotes/${kid}/access`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Granted access to ${data.user?.name || 'member'}`);
      await loadKeynotes();
      // Keep dialog open, with refreshed keynote data.
      setAccessKeynote(prev => {
        const refreshed = (keynotes || []).find(k => (k.keynote_id || k.file_id) === kid);
        return refreshed || prev;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to grant');
    } finally {
      setAccessBusy(false);
    }
  };

  const revokeAccess = async (userId) => {
    if (!accessKeynote) return;
    setAccessBusy(true);
    const kid = accessKeynote.keynote_id || accessKeynote.file_id;
    try {
      const res = await fetch(`${API}/api/admin/keynotes/${kid}/access/${userId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Access revoked');
      await loadKeynotes();
    } catch (e) {
      toast.error(e.message || 'Failed to revoke');
    } finally {
      setAccessBusy(false);
    }
  };

  // Refresh accessKeynote when keynotes list changes (after a grant/revoke).
  useEffect(() => {
    if (!accessKeynote) return;
    const kid = accessKeynote.keynote_id || accessKeynote.file_id;
    const fresh = keynotes.find(k => (k.keynote_id || k.file_id) === kid);
    if (fresh && fresh !== accessKeynote) setAccessKeynote(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keynotes]);

  const filteredUsers = useMemo(() => {
    const q = accessSearch.trim().toLowerCase();
    const grantedIds = new Set((accessKeynote?.manual_access_user_ids) || []);
    return accessUsers
      .filter(u => u.user_id && !grantedIds.has(u.user_id))
      .filter(u => !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [accessUsers, accessSearch, accessKeynote]);

  const resetForm = () => {
    setForm({ title: '', presenter: '', company: '', event_name: '', event_date: '', subject: '', description: '', video_url: '', file: null });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.file && !form.video_url.trim()) { toast.error('Upload a file or provide a video URL'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('presenter', form.presenter);
      fd.append('company', form.company);
      fd.append('event_name', form.event_name);
      fd.append('event_date', form.event_date);
      fd.append('subject', form.subject);
      fd.append('description', form.description);
      fd.append('video_url', form.video_url);
      if (form.file) fd.append('file', form.file);

      const res = await fetch(`${API}/api/keynotes/upload`, { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
      toast.success('Keynote uploaded successfully');
      setShowUpload(false);
      resetForm();
      loadKeynotes();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!(await window.appConfirm('Delete this keynote permanently?'))) return;
    try {
      const res = await fetch(`${API}/api/keynotes/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Keynote deleted');
      loadKeynotes();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-keynotes-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Keynote Presentations</h2>
          <p className="text-sm text-slate-500">{keynotes.length} keynote{keynotes.length !== 1 ? 's' : ''} uploaded</p>
        </div>
        <Button data-testid="add-keynote-btn" onClick={() => setShowUpload(true)}
          className="bg-brand-navy hover:bg-brand-dark text-white">
          <Plus className="h-4 w-4 mr-2" /> Add Keynote
        </Button>
      </div>

      {/* Pending Access Requests panel — surfaces member requests so admins
          can see WHO asked + grant or dismiss in one click. Auto-hides when
          there are no pending entries to keep the page tidy. */}
      {!requestsLoading && accessRequests.filter(r => r.status === 'pending').length > 0 && (
        <Card className="p-4 sm:p-5 border-amber-200 bg-amber-50/40" data-testid="keynote-access-requests-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
              <BellRing className="h-4 w-4" />
              Pending access requests
              <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-xs font-semibold" data-testid="pending-request-count">
                {accessRequests.filter(r => r.status === 'pending').length}
              </span>
            </h3>
          </div>
          <div className="space-y-2">
            {accessRequests.filter(r => r.status === 'pending').map(req => (
              <div key={req.request_id}
                data-testid={`keynote-request-${req.request_id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white rounded-lg border border-amber-100">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{req.current_name}</p>
                    <p className="text-xs text-slate-500 truncate">{req.current_email}</p>
                  </div>
                  {(req.user_role || req.user_organisation) && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {req.user_role}{req.user_role && req.user_organisation ? ' · ' : ''}{req.user_organisation}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Requested {new Date(req.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {req.existing_grants > 0 && (
                      <span className="ml-2 text-emerald-600">· already has {req.existing_grants} keynote grant{req.existing_grants !== 1 ? 's' : ''}</span>
                    )}
                    {req.user_status === 'suspended' && (
                      <span className="ml-2 text-rose-600">· account suspended</span>
                    )}
                  </p>
                  {req.reason && (
                    <p className="text-xs text-slate-600 mt-1.5 italic">"{req.reason}"</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={requestBusyId === req.request_id}
                    onClick={() => handleDismissRequest(req)}
                    data-testid={`dismiss-request-${req.request_id}`}
                    className="text-slate-600 border-slate-200 hover:bg-slate-50">
                    <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                  </Button>
                  <Button
                    size="sm"
                    disabled={requestBusyId === req.request_id || req.user_status === 'suspended'}
                    onClick={() => handleGrantRequest(req)}
                    data-testid={`grant-request-${req.request_id}`}
                    className="bg-brand-sage hover:bg-brand-sage/90 text-white">
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    {requestBusyId === req.request_id ? 'Granting…' : 'Grant access'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-700/80 mt-3">
            Granting access adds this member to <span className="font-semibold">every keynote</span>. To grant a single keynote only, use the per-keynote <Check className="inline h-3 w-3" /> "Manage access" button below instead.
          </p>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Upload Keynote</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title *</label>
              <Input data-testid="keynote-title" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. The Future of Sustainable Supply Chains" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Presenter / Author *</label>
                <Input data-testid="keynote-presenter" value={form.presenter}
                  onChange={e => setForm(f => ({ ...f, presenter: e.target.value }))}
                  placeholder="e.g. Kelly Hobson" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Company</label>
                <Input data-testid="keynote-company" value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="e.g. ShapeTomorrow" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Event</label>
                <Input data-testid="keynote-event" value={form.event_name}
                  onChange={e => setForm(f => ({ ...f, event_name: e.target.value }))}
                  placeholder="e.g. SLL Manchester 2026" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date</label>
                <Input data-testid="keynote-date" type="date" value={form.event_date}
                  onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Subject / Topic</label>
              <Input data-testid="keynote-subject" value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="e.g. Net Zero Logistics, EV Fleet Strategy" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea data-testid="keynote-description" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief summary of the presentation" rows={3} />
            </div>
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-slate-700">Upload file or provide a video link</p>
              <div>
                <label className="text-sm text-slate-600">Presentation / Document file</label>
                <Input data-testid="keynote-file" type="file"
                  accept=".pdf,.pptx,.ppt,.doc,.docx,.key,.mp4,.mov,.avi"
                  onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                  className="mt-1" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">OR</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Video URL (YouTube, Vimeo, etc.)</label>
                <Input data-testid="keynote-video-url" value={form.video_url}
                  onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..." className="mt-1" />
              </div>
            </div>
            <Button type="submit" disabled={uploading} data-testid="submit-keynote-btn"
              className="w-full bg-brand-navy hover:bg-brand-dark text-white">
              {uploading ? 'Uploading...' : 'Upload Keynote'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manual access dialog */}
      <Dialog open={!!accessKeynote} onOpenChange={(o) => { if (!o) setAccessKeynote(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="keynote-access-dialog">
          <DialogHeader>
            <DialogTitle>Manage access</DialogTitle>
          </DialogHeader>
          {accessKeynote && (
            <div className="space-y-4">
              <div className="text-sm">
                <p className="font-semibold text-slate-800">{accessKeynote.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Members below can view this keynote even if they have no event attendance. Useful for historic decks (e.g. Manchester SLL) not linked to a live event.
                </p>
              </div>

              {(accessKeynote.manual_access_users || []).length > 0 ? (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Members with access</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(accessKeynote.manual_access_users || []).map(u => (
                      <Badge key={u.user_id} variant="outline"
                        data-testid={`granted-user-${u.user_id}`}
                        className="bg-emerald-50 border-emerald-200 text-emerald-800 font-normal gap-1.5 pl-2 pr-1 py-1">
                        <span className="text-xs">{u.name || u.email || u.user_id}</span>
                        <button type="button" disabled={accessBusy}
                          onClick={() => revokeAccess(u.user_id)}
                          data-testid={`revoke-access-${u.user_id}`}
                          className="text-emerald-500 hover:text-rose-600 disabled:opacity-30"
                          title="Revoke access">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs italic text-slate-400">No members have been manually granted access yet.</p>
              )}

              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Add member</p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="access-user-search"
                    value={accessSearch}
                    onChange={e => setAccessSearch(e.target.value)}
                    placeholder="Search by name or email"
                    className="pl-8 h-9"
                  />
                </div>
                <div className="mt-2 max-h-64 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {filteredUsers.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-slate-400 italic">No matching members.</p>
                  ) : filteredUsers.map(u => (
                    <button
                      key={u.user_id}
                      type="button"
                      disabled={accessBusy}
                      onClick={() => grantAccess(u.user_id)}
                      data-testid={`grant-user-${u.user_id}`}
                      className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-slate-50 transition-colors text-left disabled:opacity-50"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                      <UserPlus className="h-4 w-4 text-brand-navy flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Keynotes List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy mx-auto" />
        </div>
      ) : keynotes.length === 0 ? (
        <Card className="p-12 text-center">
          <Presentation className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No keynotes uploaded yet. Click "Add Keynote" to get started.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {keynotes.map(k => (
            <Card key={k.keynote_id || k.file_id} className="p-4" data-testid={`admin-keynote-${k.keynote_id || k.file_id}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${k.type === 'video' ? 'bg-red-50' : 'bg-brand-navy/10'}`}>
                  {k.type === 'video' ? <Video className="h-5 w-5 text-red-500" /> : <FileText className="h-5 w-5 text-brand-navy" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 truncate">{k.title}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                    {k.presenter && <span className="flex items-center gap-1"><User className="h-3 w-3" />{k.presenter}</span>}
                    {k.company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{k.company}</span>}
                    {k.event_name && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{k.event_name}</span>}
                    {k.event_date && <span>{new Date(k.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    {k.subject && <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{k.subject}</span>}
                  </div>
                  {k.description && <p className="text-xs text-slate-400 mt-1 line-clamp-1">{k.description}</p>}
                  {k.type === 'file' && k.size > 0 && (
                    <p className="text-xs text-slate-400 mt-0.5">{k.filename} ({(k.size / 1024 / 1024).toFixed(1)} MB)</p>
                  )}
                  {k.type === 'video' && k.video_url && (
                    <a href={k.video_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-brand-navy hover:underline mt-0.5 inline-block">{k.video_url}</a>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="outline" size="sm"
                    className="text-brand-navy border-brand-navy/30 hover:bg-brand-navy/5 h-8 text-xs"
                    onClick={() => openAccessDialog(k)}
                    data-testid={`manage-access-${k.keynote_id || k.file_id}`}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" />
                    Access
                    {((k.manual_access_user_ids || []).length > 0) && (
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-navy text-white text-[10px] font-bold">
                        {(k.manual_access_user_ids || []).length}
                      </span>
                    )}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(k.keynote_id || k.file_id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-keynote-${k.keynote_id || k.file_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
